import { CSSTransition } from 'react-transition-group';
import { useRef } from 'react';

import { UnstyledButton } from '~/domains/design-system/Button';
import IconX from '~/domains/design-system/icons/IconX';

import type { DialogComponentProps } from '../types';
import { TIMEOUT } from '../constants';

import { useStyles } from './DialogModal.styles';

type DialogModalProps = DialogComponentProps & {
  fullScreen?: boolean;
};

function DialogModal({
  children,
  onClose,
  fullScreen,
  wrapperContent,
  isOpen,
  isClosing,
  className,
  dataTestId,
  closeIcon,
  closeButtonClassName,
  closeButtonDataTestId,
}: DialogModalProps) {
  const { classes, cx } = useStyles({
    hasWrapperContent: !!wrapperContent,
    fullScreen,
    isClosing,
    isOpen,
  });
  const ref = useRef<HTMLDivElement>(null);

  return (
    <CSSTransition
      in={isOpen}
      appear
      timeout={TIMEOUT}
      classNames={{
        appear: 'enter',
        appearActive: 'enter-active',
        appearDone: 'enter-done',
        enter: 'enter',
        enterActive: 'enter-active',
        enterDone: 'enter-done',
        exit: 'exit',
        exitActive: 'exit-active',
        exitDone: 'exit-done',
      }}
      nodeRef={ref}
    >
      <div
        ref={ref}
        role="dialog"
        data-ds-dialog
        className={cx(classes.modal, className)}
        data-test-id={dataTestId}
      >
        {wrapperContent && <div className={classes.wrapperContent}>{wrapperContent}</div>}
        <div className={classes.containerInner}>
          {onClose && (
            <UnstyledButton
              className={cx(classes.closeButton, closeButtonClassName)}
              onClick={onClose}
              data-test-id={closeButtonDataTestId}
            >
              {closeIcon || <IconX />}
            </UnstyledButton>
          )}
          {children}
        </div>
      </div>
    </CSSTransition>
  );
}

export default DialogModal;
